import { FalkorDB } from 'falkordb';
import { config } from '../config';

interface ApiKeyVerificationResult {
  valid: boolean;
  data: any; // Or a more specific type if known
  message: string;
}

class FalkorDBService {
  private client: FalkorDB | null = null;
  private apiKey: string | null = null;
  private apiKeyVerificationResult: ApiKeyVerificationResult | null = null;

  constructor() {
    // this.init();
  }

  /**
   * Returns the API key for MCP
   * @returns API key as string
   */
  public async verifyApiKey(apiKey: string): Promise<ApiKeyVerificationResult> {
    // if the API key is the same as the one we already verified, return the result
    if (this.apiKey === apiKey) {
      return {
        valid: true,
        data: this.apiKeyVerificationResult,
        message: "API KEY verification successful"
      };
    }

    const myHeaders = new Headers();
    myHeaders.append("User-Agent", "Apidog/1.0.0 (https://apidog.com)");
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", "Bearer eyJhbGciOiJIUzI1NiJ9.e30.Ivww_qG4htViuqxXGRVbHVZbMQETOYnfbYf06HsS_B4");
    myHeaders.append("Accept", "*/*");
    myHeaders.append("Host", "lihiuvyxjttzpdhzafcb.supabase.co");
    myHeaders.append("Connection", "keep-alive");

    const raw = JSON.stringify({
      "apiKey": apiKey
    });

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: raw,
      redirect: 'follow' as RequestRedirect,
    };

    try {
      const response = await fetch("https://lihiuvyxjttzpdhzafcb.supabase.co/functions/v1/check-api-key", requestOptions)
      const result = await response.json();
      // console.log('API Key Verification Result:', result);
      
      if (!response.ok) {
        throw new Error("Error Status " + response.status.toString());
      }

      this.apiKeyVerificationResult = result;
      this.apiKey = apiKey;
      
      // initialize the client with valid credentials
      // console.error('APi RESULT',JSON.stringify(this.apiKeyVerificationResult));
      const serverConnectionStatus = await this.init();
      if (!serverConnectionStatus) {
        this.apiKeyVerificationResult = null;
        this.apiKey = null;
        return {
          valid: false,
          data: {},
          message: "API KEY verification successful, EE MCP Server Connection Failed"
        };
      }

      return {
        valid: true,
        data: result,
        message: "API KEY verification successful, EE MCP Server Connected"
      };

    } catch (error) {
      let errorMessage = "Unknown error occurred during API key verification";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      this.apiKeyVerificationResult = null;
      this.apiKey = null;
      return {
        valid: false,
        data: {},
        message: "API KEY verification failed: " + errorMessage
      };
    }
  }

  private async init(): Promise<boolean> {
    try {
      // if the API key is the same as the one we already verified, return the result
      if (this.apiKey) {
        config.falkorDB.password = this.apiKeyVerificationResult?.data?.['FALKORDB_PASSWORD'];
        config.falkorDB.username = this.apiKeyVerificationResult?.data?.['FALKORDB_USERNAME'];
        config.falkorDB.host = this.apiKeyVerificationResult?.data?.['FALKORDB_HOST'];
        config.falkorDB.port = this.apiKeyVerificationResult?.data?.['FALKORDB_PORT'];
       
      } else {
        this.apiKeyVerificationResult = null;
        this.apiKey = null;
        throw new Error('Missing API key');
      }
      if (this.client) {
        await this.client.close();
        this.client = null;
      }
      
      this.client = await FalkorDB.connect({
        socket: {
          host: config.falkorDB.host,
          port: config.falkorDB.port,
        },
        password: config.falkorDB.password,
        username: config.falkorDB.username,
      });

      // Test connection
      const connection = await this.client.connection;
      await connection.ping();
      // return {
      //   content: [{
      //     type: "text",
      //     text: JSON.stringify({
      //       status: "success",
      //       message: "Successfully connected to EE MCP Server"
      //     })
      //   }]
      // };
      return true;
    } catch (error) {
      return false;
      // console.error('Failed to connect to EE MCP Server', error);
      // console.error(config.falkorDB);
      // Retry connection after a delay
      // setTimeout(() => this.init(), 5000);
    }
  }

  async executeGraphQuery(query: string, apiKey: string = config.mcp.apiKey, params?: Record<string, any>): Promise<any> {
    if (!query || query.trim() === '') {
      return {error:true, result:`Error executing EE MCP Server Graph DB query: No Query Provided`};
    }
    const connectionParameters = await falkorDBService.verifyApiKey(apiKey);
    if (connectionParameters.valid && this.client !== null) {
      const graphName = this.apiKeyVerificationResult?.data?.['FALKORDB_GRAPH_DEFAULT'];
      try {
        const graph = this.client.selectGraph(graphName);
        const result = await graph.query(query, params);
        return {error:false, result:result};
      } catch (error) {
        // const sanitizedGraphName = graphName?.replace(/\n|\r/g, "") ?? "unknown";
        throw new Error(`Error executing EE MCP Server Graph DB query: ${error}`);
      }
    } else {
      throw new Error(`Failed to get Ontology from EE MCP Server: ${connectionParameters.message}`);
    } // Ensure API key is valid before proceeding
  }

  async executeOntologyGraphQuery(query: string, apiKey: string = config.mcp.apiKey, params?: Record<string, any>): Promise<any> {
    if (!query || query.trim() === '') {
      return {error:true, result:`Error executing EE MCP Server Ontology Graph DB query: No Query Provided`};
    }
    const connectionParameters = await falkorDBService.verifyApiKey(apiKey);
    if (connectionParameters.valid && this.client !== null) {
      const graphName = this.apiKeyVerificationResult?.data?.['FALKORDB_GRAPH_SEMANTIC'];
      try {
        const graph = this.client.selectGraph(graphName);
        const result = await graph.query(query, params);
        return {error:false, result:result};
      } catch (error) {
        throw new Error(`Error executing EE MCP Server Ontology Graph DB query: ${error}`);
      }
    } else {
      throw new Error(`Failed to get Ontology Data from EE MCP Server: ${connectionParameters.message}`);
    } // Ensure API key is valid before proceeding
  }

  async getOntology(apiKey: string = config.mcp.apiKey, params?: Record<string, any>): Promise<any> {
    const connectionParameters = await falkorDBService.verifyApiKey(apiKey);
    if (connectionParameters.valid && this.client !== null) {
      // get the schema name from the api key verification result
      const schemaName = this.apiKeyVerificationResult?.data?.['FALKORDB_GRAPH_SEMANTIC'];
      const graphName = schemaName.endsWith('_schema') ? schemaName : schemaName + '_schema';
      try {
        const graph = this.client.selectGraph(graphName);
        const result = await graph.query('MATCH (sourceNode)-[relation]->(targetNode) RETURN sourceNode, relation, targetNode', params);
        if (!result.data || result.data.length === 0) {
          throw new Error('No Ontology found in EE MCP Server');
        }
        return result;
      } catch (error) {
        throw new Error(`Error getting Ontology from EE MCP Server: ${error}`);
      }
    } else {
      throw new Error(`Failed to get Ontology from EE MCP Server: ${connectionParameters.message}`);
    }
  } 

  async getOntologyMetadata(apiKey: string = config.mcp.apiKey, params?: Record<string, any>): Promise<any> {
    const connectionParameters = await falkorDBService.verifyApiKey(apiKey);
    if (connectionParameters.valid && this.client !== null) {
      // get the schema name from the api key verification result
      const schemaName = this.apiKeyVerificationResult?.data?.['FALKORDB_GRAPH_SEMANTIC'];
      const graphName = schemaName
      try {
        const graph = this.client.selectGraph(graphName);
        const result = await graph.query("MATCH (metadata:MetaInfo) RETURN metadata.Name,metadata.Prompt", params);
        if (!result.data || result.data.length === 0) {
          throw new Error('No Ontology Metadata found in EE MCP Server');
        }
        const instructions = result.data.map((item: any) => `${item['metadata.Name']}: ${item['metadata.Prompt']}`).join('\n');
        return instructions;
      } catch (error) {
        throw new Error(`Error getting Ontology Metadata from EE MCP Server: ${error}`);
      }
    } else {
      throw new Error(`Failed to get Ontology Metadata from EE MCP Server: ${connectionParameters.message}`);
    }
  } 

  /**
   * Lists all available graphs in FalkorDB
   * @returns Array of graph names
   */
  async listGraphs(apiKey: string = config.mcp.apiKey): Promise<string[]> {
    const connectionParameters = await falkorDBService.verifyApiKey(apiKey);
    if (connectionParameters.valid && this.client !== null) {
      try {
        return await this.client.list();
      } catch (error) {
        console.error('Error listing EE Graphs:', error);
        throw error;
      }
    } else {
      // Throw an error if the API key is invalid
      throw new Error(`Failed to list graphs: ${connectionParameters.message}`);
    }
  }

  async close() {
    if (this.client) {
      await this.client.close();
      this.client = null;
    }
  }
}

// Export a singleton instance
export const falkorDBService = new FalkorDBService();